import ArrowForwardIosOutlinedIcon from '@mui/icons-material/ArrowForwardIosOutlined';
import { Box, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import ArticlesApi from '../../../apis/ArticlesApi';
import ArticleCard from '../../../components/ArticleCard/ArticleCard';
import LoadingCircle from '../../../components/LoadingCircle/LoadingCircle';
import { ArticleCardDto } from '../../../generated-types/articleCardDto';
import useApi from '../../../lib/useApi';
import Section from '../components/Section';
import SectionTypography from '../components/SectionTypography';

/**
 * A section within the training page to show recent articles
 */
const ArticlesSection = () => {
  const theme = useTheme();
  const [articles, setArticles] = useState<ArticleCardDto[]>([]);
  const [loadError, setLoadError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const articlesApi = useApi(ArticlesApi);

  useEffect(() => {
    setLoadError(false);
    setIsLoading(true);
    articlesApi
      .GetArticleCards()
      .then((all) => {
        const latestArticles = all
          .sort(
            (a, b) =>
              new Date(b.written).getTime() - new Date(a.written).getTime()
          ) // Sort by date descending
          .slice(0, 3); // Take the first 3 articles
        setArticles(latestArticles);
      })
      .catch(() => setLoadError(true))
      .finally(() => setIsLoading(false));
  }, [articlesApi]);

  return (
    <Section
      title="Recent Articles"
      rightSection={
        <Box
          display="flex"
          alignItems="center"
          to="/articles"
          component={RouterLink}
          sx={{
            textDecoration: 'none',
          }}
        >
          <SectionTypography title="More" />
          <ArrowForwardIosOutlinedIcon
            sx={{
              marginLeft: '.5em',
              fontSize: '1em',
              color: theme.palette.text.secondary,
            }}
          />
        </Box>
      }
    >
      <Box position="relative" minHeight="6em">
        {articles.map((a, i) => (
          <ArticleCard key={i} {...a} />
        ))}
        {articles.length == 0 && !isLoading && (
          <Typography>
            No articles have been loaded. Try again later!
          </Typography>
        )}
        {loadError && (
          <Typography>
            An error occurred while trying to load the articles. Please refresh
            your page to try again.
          </Typography>
        )}
        <LoadingCircle
          isLoading={isLoading}
          hideBackgroundShadow
          loadingText="Loading articles..."
        />
      </Box>
    </Section>
  );
};

export default ArticlesSection;
